import { OrganizationUserType, UserStatusType } from '@mfw/common';
import { useQueryClient } from '@tanstack/react-query';

import type {
  InvitationActionType,
  ApiInvitation,
} from '../../schemas/invitation.schema';
import type { BaseApiErrorResponse } from '../../api/types/api.types';

import { useOrganizationContext } from '../../context/OrganizationProvider/OrganizationProvider';
import { isBlockingPendingQuestionnaire } from '../questionnaireSubmissions';
import { updateInvitation } from '../../api/invitation.api';
import { useNavigate } from '../../router/hooks';
import { useAuthContext } from '../../context';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';

interface UpdateInvitationArgs {
  organizationUserId: string;
  type: InvitationActionType;
  // Required only when accepting an invite that carries a RECURRING
  // membership — see `InvitationModal`'s pre-accept Terms of Use step.
  touVersionId?: string;
  // 6.10 — required when the organization asks for its terms on connecting.
  connectionTouVersionId?: string;
}

interface UseUpdateInvitationOptions {
  onClientOnboardingRequired?: () => void;
}

export default function useUpdateInvitation(
  options?: UseUpdateInvitationOptions,
) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refetchCurrentUser } = useAuthContext();
  const { clearOrganization, selectOrganization } = useOrganizationContext();

  const response = useMutation<
    ApiInvitation | null,
    BaseApiErrorResponse<UpdateInvitationArgs>,
    UpdateInvitationArgs
  >({
    mutationFn: async ({
      organizationUserId,
      type,
      touVersionId,
      connectionTouVersionId,
    }: UpdateInvitationArgs) => {
      return updateInvitation(
        organizationUserId,
        type,
        touVersionId,
        connectionTouVersionId,
      );
    },
    onSuccess: async (data, { type }) => {
      queryClient.setQueryData(
        queryKeys.invitations.fetchInvitationByUserId().queryKey,
        null,
      );
      const updatedUser = await refetchCurrentUser();
      if (type === 'accept' && data?.organizationId) {
        selectOrganization({ id: data.organizationId });
        // `data.type` is this invite's own OrganizationUser type.
        // `updatedUser.type` is the role of whatever membership happens to be
        // first on the account, so keying off it made a trainer-somewhere
        // accepting a CLIENT invite skip these branches entirely.
        const isClientInvite = data.type === OrganizationUserType.CLIENT;
        if (
          isClientInvite &&
          updatedUser?.status === UserStatusType.ACTIVE &&
          !updatedUser?.fitnessProfile &&
          !updatedUser?.hasFinishedOnboarding
        ) {
          // Brand-new client: the full onboarding dialog/wizard already ends
          // with a pending-questionnaires step, so any questionnaire attached
          // to this invite is handled inside that flow.
          options?.onClientOnboardingRequired?.();
        } else if (
          isClientInvite &&
          updatedUser?.status === UserStatusType.ACTIVE
        ) {
          // Already-onboarded client accepting a (possibly different) org's
          // invite: accepting may have attached new questionnaire submissions
          // server-side (questionnaire-submissions.service.ts). Fetch fresh
          // (not just invalidate) so we can forward straight into the wizard
          // in this same tick, rather than accept doing nothing and leaving
          // it to an incidental future re-render to notice and redirect.
          // A fetch failure must not fail the flow: the invite is already
          // accepted server-side, and rejecting out of onSuccess would turn
          // that success into an unhandled rejection in the invitation modal.
          // Treated as "nothing blocking" instead — the authenticated guard
          // re-evaluates pending submissions itself, so the wizard still
          // catches up on its next evaluation.
          const pendingQuestionnaireSubmissions = await queryClient
            .fetchQuery({
              ...queryKeys.questionnaireSubmissions.fetchPending(),
              // The list is fetched right after login, so at this point it is
              // usually still within the default staleTime — a cache hit here
              // would return the pre-accept list and hide the freshly attached
              // submissions until a full reload.
              staleTime: 0,
            })
            .catch(() => null);
          // Only the blocking ones justify the wizard — the onboarding step
          // itself filters by the same predicate, so redirecting on a merely
          // scheduled questionnaire would bounce straight back to "/".
          if (
            pendingQuestionnaireSubmissions?.some(
              isBlockingPendingQuestionnaire,
            )
          ) {
            navigate('/auth/onboarding/client');
          }
        }
        // F4b — TRAINER/OWNER(admin) invites: redirect to the org-scoped
        // member-profile edit form (prefilled via the template endpoint).
        // Deliberately keyed off `data.type` (this invite's own
        // OrganizationUser type), not the CLIENT-only check above — a
        // CLIENT invite never hits this branch.
        if (
          data.type === OrganizationUserType.TRAINER ||
          data.type === OrganizationUserType.OWNER
        ) {
          navigate('/organizations/:organizationId/members/me/profile/edit', {
            params: { organizationId: data.organizationId },
          });
        }
      } else {
        clearOrganization();
      }
    },
  });

  return response;
}
